/**
 * User Management Component
 * Admin view for listing, creating, editing, and deleting users.
 */

import { EmptyState } from '../../components/empty-state';
import { SearchInput } from '../../components/search-input';
import { Modal } from '../../components/modal';
import { Table } from '../../components/table';
import { CheckRow, Checkbox, FormBlock, FormField, FormMessage, Input, Select } from '../../components/form-field';
import { saveFailure, type FormFailure } from './save-failure-words';
import { useMessageInSight } from '../../components/use-message-in-sight';
import { showToast } from '../../components/toast/toast-store';
import { activeTone, roleTone } from './status-tone';
import { Pill } from '../../components/pill';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type { UserListItem, CreateUserPayload, UpdateUserPayload } from '../../api/admin/users/types';
import { ProfilePhotoField } from '../shell/profile-photo-field';
import {
  useCreateUser,
  useUpdateUser,
  useUpdateUserPassword,
  useUploadUserPhoto,
} from '../../api/admin/users/hooks';
import { useAdminCohorts, useAddCohortStudents, useRemoveCohortStudents } from '../../api/admin/cohorts/hooks';
import { LocationPicker } from './location-picker';
import { useUserList, type UserTypeFilter } from './use-user-list';
import admin from './admin-dashboard.module.css';

interface UserManagementProps {
  onBack: () => void;
}


export function UserManagement({ onBack }: UserManagementProps) {
  const { t } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);

  // Which users are on screen and which of them are ticked.
  const {
    page,
    setPage,
    typeFilter,
    setTypeFilter,
    searchText,
    setSearchText,
    users,
    filteredUsers,
    total,
    totalPages,
    isLoading,
    isDeleting,
    error,
    selectedIds,
    allSelected,
    someSelected,
    reload,
    handleDeleteSingle,
    handleDeleteSelected,
    toggleSelectAll,
    toggleSelect,
  } = useUserList();

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleCreated = () => {
    setShowCreateForm(false);
    setPage(1);
    reload();
  };

  const handleUpdated = () => {
    setEditingUser(null);
    reload();
  };

  const roleLabel = (userType: string) => {
    switch (userType) {
      case 'PONDER_ADMIN': return t('admin.users.roles.admin');
      case 'MODERATOR': return t('admin.users.roles.moderator');
      default: return t('admin.users.roles.student');
    }
  };

  return (
    <div className="portal-shell">
      <CommonHeader
        title={t('admin.users.title')}
        showBackButton
        onBackClick={onBack}
      />

      <main className={admin.adminPanelMain}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>
              {t('admin.users.totalCount', { count: total })}
            </h2>
            <div className={admin.adminFilterBar}>
              {someSelected && (
                <Button type="button" variant="dangerQuiet" onClick={() => void handleDeleteSelected()}
                  disabled={isDeleting}
                >
                  {t('admin.users.deleteSelected', { count: selectedIds.size })}
                </Button>
              )}
              <Select
                className={admin.adminFilter}
                aria-label={t('common.filter')}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as UserTypeFilter)}
              >
                <option value="">{t('admin.users.filterAll')}</option>
                <option value="STUDENT">{t('admin.users.roles.student')}</option>
                <option value="MODERATOR">{t('admin.users.roles.moderator')}</option>
                <option value="PONDER_ADMIN">{t('admin.users.roles.admin')}</option>
              </Select>
              <Button type="button" onClick={() => setShowCreateForm(true)}
              >
                + {t('admin.users.createUser')}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className={admin.adminLoading}>{t('common.loading')}</div>
          ) : users.length === 0 ? (
            !error && <EmptyState icon="👤">{t('admin.users.noUsers')}</EmptyState>
          ) : (
            <>
              <div className={admin.adminSearchBar}>
                <SearchInput
            placeholder={t('common.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
                {searchText && (
                  <span className={admin.adminSearchResults}>
                    {t('common.searchResults', { count: filteredUsers.length, total: users.length })}
                  </span>
                )}
              </div>
              <Table flush>
              <thead>
                <tr>
                  <th className={admin.adminTableCheckCol}>
                    <Checkbox
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label={t('admin.users.selectAll')}
                      title={t('admin.users.selectAll')}
                    />
                  </th>
                  <th>{t('admin.users.columns.name')}</th>
                  <th>{t('admin.users.columns.email')}</th>
                  <th>{t('admin.users.columns.role')}</th>
                  <th>{t('admin.users.columns.status')}</th>
                  <th>{t('admin.users.columns.cohorts')}</th>
                  <th>{t('admin.users.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} aria-selected={selectedIds.has(user.id)}>
                    <td>
                      <Checkbox
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        aria-label={t('common.selectRow')}
                      />
                    </td>
                    <td>
                      <div className={`font-medium ${admin.adminTextStrong}`}>{user.firstName} {user.lastName}</div>
                      {user.username && (
                        <div className={`text-xs ${admin.adminTextMuted}`}>@{user.username}</div>
                      )}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <Pill tone={roleTone(user.userType)}>
                        {roleLabel(user.userType)}
                      </Pill>
                    </td>
                    <td>
                      <Pill tone={activeTone(user.isActive)}>
                        {user.isActive ? t('admin.users.active') : t('admin.users.inactive')}
                      </Pill>
                    </td>
                    <td>
                      {user.studentProfile?.cohorts && user.studentProfile.cohorts.length > 0
                        ? user.studentProfile.cohorts.map((c) => c.name).join(', ')
                        : '-'}
                    </td>
                    <td>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingUser(user)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleDeleteSingle(user)}
                          disabled={isDeleting}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}

          {totalPages > 1 && (
            <div className={admin.adminPagination}>
              <span>
                {t('admin.pagination.showing', { page, totalPages })}
              </span>
              <div className={admin.adminPaginationButtons}>
                <Button type="button" variant="ghost" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}
                >
                  {t('admin.pagination.prev')}
                </Button>
                <Button type="button" variant="ghost" size="sm" disabled={page>= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  {t('admin.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreateForm && (
        <CreateUserForm
          onClose={() => setShowCreateForm(false)}
          onCreated={handleCreated}
        />
      )}

      {editingUser && (
        <EditUserForm
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
}

interface CreateUserFormProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateUserForm({ onClose, onCreated }: CreateUserFormProps) {
  const { t } = useTranslation();
  const createUser = useCreateUser();
  const uploadPhoto = useUploadUserPhoto();
  const isSubmitting = createUser.isPending;
  const [error, setError] = useState<FormFailure | null>(null);
  const { messageRef, actionsRef } = useMessageInSight(error);
  // Photo is uploaded after the user is created (we need the generated id/nakamaCustomId).
  const [pendingPhoto, setPendingPhoto] = useState<Blob | null>(null);
  const [form, setForm] = useState<Omit<CreateUserPayload, 'cohortId'>>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    userType: 'STUDENT',
    username: '',
    birthdayDate: '',
    addressCountry: '',
    addressState: '',
    addressCity: '',
  });

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    let created: Awaited<ReturnType<typeof createUser.mutateAsync>>;
    try {
      created = await createUser.mutateAsync({
        ...form,
        birthdayDate: form.userType === 'STUDENT' ? (form.birthdayDate || undefined) : undefined,
        addressCountry: form.userType === 'STUDENT' ? (form.addressCountry || undefined) : undefined,
        addressState: form.userType === 'STUDENT' ? (form.addressState || undefined) : undefined,
        addressCity: form.userType === 'STUDENT' ? (form.addressCity || undefined) : undefined,
      });
    } catch (e) {
      setError(saveFailure(t, e, 'user'));
      return;
    }
    // Upload the chosen photo now that the user (and its nakamaCustomId) exists.
    // The user is saved by then, so a failed upload closes the form all the same,
    // where a second Save would create them twice, and says so in a toast.
    if (pendingPhoto) {
      try {
        await uploadPhoto.mutateAsync({ id: created.id, blob: pendingPhoto });
      } catch (e) {
        const failure = saveFailure(t, e, 'userPhoto');
        showToast('error', failure.title, failure.detail ?? undefined);
      }
    }
    onCreated();
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={t('admin.users.createUser')}>
      <form onSubmit={handleSubmit}>
        <div className={admin.adminFormRow}>
          <FormField label={t('admin.users.form.firstName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t('admin.users.form.lastName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
              />
            )}
          </FormField>
        </div>
        <FormField label={t('admin.users.form.email')} required>
          {(field) => (
            <Input {...field}
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          )}
        </FormField>
        <FormField label={t('admin.users.form.username')} required>
          {(field) => (
            <Input {...field}
              type="text"
              value={form.username}
              onChange={(e) => handleChange('username', e.target.value)}
              required
            />
          )}
        </FormField>
        <FormField label={t('admin.users.form.password')} required>
          {(field) => (
            <Input {...field}
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required
              minLength={8}
            />
          )}
        </FormField>
        <FormField label={t('admin.users.form.userType')} required>
          {(field) => (
            <Select {...field}
              value={form.userType}
              onChange={(e) => handleChange('userType', e.target.value)}
            >
              <option value="STUDENT">{t('admin.users.roles.student')}</option>
              <option value="MODERATOR">{t('admin.users.roles.moderator')}</option>
              <option value="PONDER_ADMIN">{t('admin.users.roles.admin')}</option>
            </Select>
          )}
        </FormField>

        <FormBlock label={t('admin.users.form.photo.label')}>
          <ProfilePhotoField onPendingChange={setPendingPhoto} /></FormBlock>

        {form.userType === 'STUDENT' && (
          <>
            <FormField label={t('admin.users.form.birthday')}>
              {(field) => (
                <Input {...field}
                  type="date"
                  value={form.birthdayDate}
                  onChange={(e) => handleChange('birthdayDate', e.target.value)}
                />
              )}
            </FormField>
            <LocationPicker
              country={form.addressCountry ?? ''}
              state={form.addressState ?? ''}
              city={form.addressCity ?? ''}
              onCountryChange={(v) => handleChange('addressCountry', v)}
              onStateChange={(v) => handleChange('addressState', v)}
              onCityChange={(v) => handleChange('addressCity', v)}
            />
          </>
        )}

        {/* Above Save, kept in sight as it comes (useMessageInSight): the form's top scrolls out of sight. */}
        {error && (
          <div ref={messageRef} tabIndex={-1} role="group" aria-label={error.title ?? error.detail ?? undefined}>
            <FormMessage title={error.title}>{error.detail}</FormMessage>
          </div>
        )}
        <div ref={actionsRef} className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface EditUserFormProps {
  user: UserListItem;
  onClose: () => void;
  onUpdated: () => void;
}

function EditUserForm({ user, onClose, onUpdated }: EditUserFormProps) {
  const { t } = useTranslation();
  const updateUser = useUpdateUser();
  const addCohortStudents = useAddCohortStudents();
  const removeCohortStudents = useRemoveCohortStudents();
  const updatePassword = useUpdateUserPassword();
  const isSubmitting = updateUser.isPending || updatePassword.isPending;
  const [error, setError] = useState<FormFailure | null>(null);
  const { messageRef, actionsRef } = useMessageInSight(error);
  const [form, setForm] = useState<UpdateUserPayload & { isActive: boolean; username: string }>({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email ?? '',
    username: user.username ?? '',
    isActive: user.isActive,
    addressCountry: user.studentProfile?.addressCountry ?? '',
    addressState: user.studentProfile?.addressState ?? '',
    addressCity: user.studentProfile?.addressCity ?? '',
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Photo is uploaded/removed immediately (separate from the Save button).
  const [photoUrl, setPhotoUrl] = useState<string | null>(user.avatarPhotoUrl ?? null);

  // Cohort management (students only) — only fetched when editing a student.
  const cohortsQuery = useAdminCohorts(user.userType === 'STUDENT');
  const allCohorts = cohortsQuery.data ?? [];
  const [selectedCohortIds, setSelectedCohortIds] = useState<Set<string>>(
    new Set(user.studentProfile?.cohorts.map((c) => c.id) ?? []),
  );
  const [cohortSearch, setCohortSearch] = useState('');

  const toggleCohort = (cohortId: string) =>
    setSelectedCohortIds((prev) => {
      const next = new Set(prev);
      if (next.has(cohortId)) next.delete(cohortId);
      else next.add(cohortId);
      return next;
    });

  const filteredCohorts = allCohorts.filter((c) => {
    const q = cohortSearch.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q);
  });

  const handleChange = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword && newPassword !== confirmPassword) {
      setError({ detail: t('admin.users.form.passwordMismatch') });
      return;
    }
    if (newPassword && newPassword.length < 8) {
      setError({ detail: t('admin.users.form.passwordTooShort') });
      return;
    }
    setError(null);
    try {
      await updateUser.mutateAsync({
        id: user.id,
        payload: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          isActive: form.isActive,
          addressCountry: user.userType === 'STUDENT' ? (form.addressCountry || undefined) : undefined,
          addressState: user.userType === 'STUDENT' ? (form.addressState || undefined) : undefined,
          addressCity: user.userType === 'STUDENT' ? (form.addressCity || undefined) : undefined,
        },
      });
      if (newPassword) {
        await updatePassword.mutateAsync({ id: user.id, newPassword });
      }
      // Sync cohort memberships via the cohort mutation hooks (they invalidate
      // the cohorts cache, not the user list).
      if (user.userType === 'STUDENT' && user.studentProfile) {
        const profileId = user.studentProfile.id;
        const originalIds = new Set(user.studentProfile.cohorts.map((c) => c.id));
        const toAdd    = [...selectedCohortIds].filter((id) => !originalIds.has(id));
        const toRemove = [...originalIds].filter((id) => !selectedCohortIds.has(id));
        await Promise.all([
          ...toAdd.map((cohortId) => addCohortStudents.mutateAsync({ id: cohortId, studentIds: [profileId] })),
          ...toRemove.map((cohortId) => removeCohortStudents.mutateAsync({ id: cohortId, studentIds: [profileId] })),
        ]);
      }
      onUpdated();
    } catch (e) {
      setError(saveFailure(t, e, 'user'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={t('admin.users.editUser')}>
      <form onSubmit={handleSubmit}>
        <div className={admin.adminFormRow}>
          <FormField label={t('admin.users.form.firstName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.firstName ?? ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t('admin.users.form.lastName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.lastName ?? ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
              />
            )}
          </FormField>
        </div>
        <FormField label={t('admin.users.form.email')} required>
          {(field) => (
            <Input {...field}
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          )}
        </FormField>
        <FormField label={t('admin.users.form.username')}>
          {(field) => (
            <Input {...field}
              type="text"
              value={form.username}
              readOnly
              disabled
            />
          )}
        </FormField>
        <CheckRow spaced>
          <Checkbox
            checked={form.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
          {t('admin.users.active')}
        </CheckRow>

        <FormBlock label={t('admin.users.form.photo.label')}>
          <ProfilePhotoField
            userId={user.id}
            currentUrl={photoUrl}
            onChange={(u) => setPhotoUrl(u.avatarPhotoUrl ?? null)}
          /></FormBlock>

        {user.userType === 'STUDENT' && (
          <>
            <LocationPicker
              country={form.addressCountry ?? ''}
              state={form.addressState ?? ''}
              city={form.addressCity ?? ''}
              onCountryChange={(v) => handleChange('addressCountry', v)}
              onStateChange={(v) => handleChange('addressState', v)}
              onCityChange={(v) => handleChange('addressCity', v)}
            />
          </>
        )}

        {/* Cohort membership section (students only) */}
        {user.userType === 'STUDENT' && (
          <>
            <div className={admin.adminFormSectionDivider}>
              <span>{t('admin.users.form.cohorts')}</span>
            </div>
            <div className={admin.adminManageSearchRow}>
              <SearchInput grow
            placeholder={t('admin.users.form.searchCohorts')}
            value={cohortSearch}
            onChange={(e) => setCohortSearch(e.target.value)}
          />
              <span className={`${admin.adminManageCount} ${admin.adminManageCountFlush}`}>
                {selectedCohortIds.size} {t('admin.users.form.cohortsSelected')}
              </span>
            </div>
            <div className={admin.adminPickList}>
              {filteredCohorts.length === 0 ? (
                <EmptyState inline>{t('common.noResults')}</EmptyState>
              ) : filteredCohorts.map((cohort) => {
                const isIn = selectedCohortIds.has(cohort.id);
                return (
                  <label key={cohort.id} className={`${admin.adminManageStudentRow} ${isIn ? admin.adminManageStudentRowIn : ''}`}>
                    <Checkbox
                      checked={isIn}
                      onChange={() => toggleCohort(cohort.id)}
                      className="w-4 h-4"
                    />
                    <span className={admin.adminManageStudentInfo}>
                      <span className={admin.adminManageStudentName}>{cohort.name}</span>
                      {cohort.description && (
                        <span className={admin.adminManageStudentMeta}>{cohort.description}</span>
                      )}
                    </span>
                    <span className={`${admin.adminManageStudentMeta} ${admin.adminManageStudentMetaFixed}`}>
                      {cohort.studentCount} {t('admin.cohorts.columns.students').toLowerCase()}
                    </span>
                    {isIn && (
                      <Pill solid tone="success">{t('admin.cohorts.member')}</Pill>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {/* Password change section */}
        <div className={admin.adminFormSectionDivider}>
          <span>{t('admin.users.form.changePassword')}</span>
        </div>
        <p className={`${admin.adminFormHint} ${admin.adminFormHintLeading}`}>
          {t('admin.users.form.changePasswordHint')}
        </p>
        <FormField label={t('admin.users.form.newPassword')}>
          {(field) => (
            <div className={admin.adminFormPasswordWrap}>
              <Input
                {...field}
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                placeholder={t('admin.users.form.newPasswordPlaceholder')}
              />
              <button
                type="button"
                className={admin.adminFormPasswordToggle}
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          )}
        </FormField>
        <FormField
          label={t('admin.users.form.confirmPassword')}
          error={
            newPassword && confirmPassword && newPassword !== confirmPassword
              ? t('admin.users.form.passwordMismatch')
              : undefined
          }
        >
          {(field) => (
            <Input
              {...field}
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={t('admin.users.form.confirmPasswordPlaceholder')}
            />
          )}
        </FormField>

        {/* Above Save, kept in sight as it comes (useMessageInSight): the form's top scrolls out of sight. */}
        {error && (
          <div ref={messageRef} tabIndex={-1} role="group" aria-label={error.title ?? error.detail ?? undefined}>
            <FormMessage title={error.title}>{error.detail}</FormMessage>
          </div>
        )}
        <div ref={actionsRef} className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
